import React, {useMemo} from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import {SortableItem} from "./SortableItem";
import {useSetting} from "../../contexts/setting";

export default function SortableList({items, renderItem, className, getItemClassName, getItemStyle, useDragHandle, containerStyle}) {
  const {order, editOrder} = useSetting();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sort items based on the order in context
  const sortedItems = useMemo(() => {
    if (!order || order.length === 0) return items;

    // An id of the saved order names a place on the page: the design that
    // replaced it there takes its position (data/templateSlots.ts). An ALL
    // view item names its instance ids (`orderIds`), a template its slot's ids.
    const itemMap = new Map(
      items.flatMap((item) =>
        (item.orderIds ?? item.memberIds ?? [item.id]).map((orderId) => [orderId, item])
      )
    );
    const orderedItems = [];
    const seenIds = new Set();

    // Add items that are in the order list and exist in the current items
    order.forEach((id) => {
      const item = itemMap.get(id);
      if (item && !seenIds.has(item.id)) {
        orderedItems.push(item);
        seenIds.add(item.id);
      }
    });

    // Add remaining items that were not in the order list
    items.forEach((item) => {
      if (!seenIds.has(item.id)) {
        orderedItems.push(item);
      }
    });

    return orderedItems;
  }, [items, order]);

  const handleDragEnd = (event) => {
    const {active, over} = event;
    
    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
      const newIndex = sortedItems.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newSortedItems = arrayMove(sortedItems, oldIndex, newIndex);
        const newOrder = newSortedItems.map((item) => item.id);
        
        // Check if new order matches the default order (items prop)
        const defaultOrderIds = items.map(item => item.id);
        const isDefault = newOrder.length === defaultOrderIds.length && 
                          newOrder.every((id, index) => id === defaultOrderIds[index]);

        if (isDefault) {
          editOrder([]); // Remove from URL
        } else {
          editOrder(newOrder);
        }
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortedItems.map((item) => item.id)}
        strategy={rectSortingStrategy}
      >
        <div style={{display: 'flex', flexWrap: 'wrap', gap: '15px', width: '100%', ...containerStyle}} className={className}>
             {sortedItems.map((item) => (
                <SortableItem 
                  key={item.id} 
                  id={item.id} 
                  className={getItemClassName ? getItemClassName(item) : undefined}
                  style={getItemStyle ? getItemStyle(item) : undefined}
                  useDragHandle={useDragHandle}
                >
                  {useDragHandle 
                    ? (handleProps) => renderItem(item, handleProps)
                    : renderItem(item)
                  }
                </SortableItem>
             ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
